import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Timestamp,
} from "firebase/firestore";

import type { DevelopmentProjectSlug } from "@/config/constants";
import { db } from "@/config/firebase";
import { firestoreErrorCode } from "@/lib/firestoreErrors";
import { logger } from "@/lib/logger";

import type { DevelopmentProject, DevelopmentUpdate } from "./types";

const PROJECTS = collection(db, "developmentProjects");
const projectDoc = (slug: DevelopmentProjectSlug) => doc(db, "developmentProjects", slug);
const updatesCol = (slug: DevelopmentProjectSlug) =>
  collection(db, "developmentProjects", slug, "updates");

export function watchDevelopmentProjects(cb: (projects: DevelopmentProject[]) => void): () => void {
  const q = query(PROJECTS, orderBy("order", "asc"));
  return onSnapshot(
    q,
    (snap) => {
      const items: DevelopmentProject[] = snap.docs.map((d) => {
        const data = d.data() as Omit<DevelopmentProject, "slug">;
        return { ...data, slug: d.id as DevelopmentProjectSlug };
      });
      cb(items);
    },
    (err) => {
      logger.warn("developmentProjects subscribe failed", {
        code: firestoreErrorCode(err),
        message: err instanceof Error ? err.message : String(err),
      });
      cb([]);
    },
  );
}

export async function upsertDevelopmentProject(
  slug: DevelopmentProjectSlug,
  patch: Omit<DevelopmentProject, "slug" | "updatedAt">,
): Promise<void> {
  await setDoc(
    projectDoc(slug),
    { ...patch, updatedAt: serverTimestamp() as unknown as Timestamp },
    { merge: true },
  );
}

export function watchDevelopmentUpdates(
  slug: DevelopmentProjectSlug,
  cb: (updates: DevelopmentUpdate[]) => void,
): () => void {
  const q = query(updatesCol(slug), orderBy("postedAt", "desc"));
  return onSnapshot(
    q,
    (snap) => {
      const items: DevelopmentUpdate[] = snap.docs.map((d) => {
        const data = d.data() as Omit<DevelopmentUpdate, "id">;
        return { id: d.id, ...data };
      });
      cb(items);
    },
    (err) => {
      logger.warn(`developmentUpdates(${slug}) subscribe failed`, {
        code: firestoreErrorCode(err),
        message: err instanceof Error ? err.message : String(err),
      });
      cb([]);
    },
  );
}

export interface DevelopmentUpdateInput {
  body: string;
  photoUrl?: string;
}

export async function createDevelopmentUpdate(
  slug: DevelopmentProjectSlug,
  input: DevelopmentUpdateInput,
): Promise<string> {
  const ref = await addDoc(updatesCol(slug), {
    ...input,
    postedAt: serverTimestamp() as unknown as Timestamp,
  });
  return ref.id;
}

export async function deleteDevelopmentUpdate(
  slug: DevelopmentProjectSlug,
  updateId: string,
): Promise<void> {
  await deleteDoc(doc(db, "developmentProjects", slug, "updates", updateId));
}
